import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Minus } from 'lucide-react';
import { T, Lang } from '../lib/i18n';
import { HexagonBg } from './HexagonBg';
export function Pricing({
  lang
}: {
  lang: Lang;
}) {
  const t = T[lang].pricing;
  return <section id="paquetes" className="bg-brand-bg-subtle py-24 px-4 md:px-8 relative overflow-hidden" data-id="element-291">
      <HexagonBg className="opacity-75" data-id="element-292" />

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-293">
        <div className="text-center mb-16" data-id="element-294">
          <div className="inline-block bg-brand-blue-primary/10 text-brand-blue-primary text-sm font-bold px-3 py-1 rounded-full mb-4" data-id="element-295">
            {t.tag}
          </div>
          <h2 className="font-display font-extrabold text-3xl md:text-5xl text-brand-navy-dark mb-4" data-id="element-296">
            {t.h2_1}
            <span className="bg-gradient-brand bg-clip-text text-transparent" data-id="element-297">
              {t.h2_2}
            </span>
          </h2>
          <p className="text-brand-text-muted max-w-xl mx-auto text-lg mb-8" data-id="element-298">
            {t.subtitle}
          </p>
          <div className="inline-block bg-red-500/10 border border-red-500/20 text-red-700 text-sm font-semibold py-1.5 px-4 rounded-full" data-id="element-299">
            {t.scarcity}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center max-w-6xl mx-auto" data-id="element-300">
          {/* Basic Plan */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white border border-brand-blue-primary/10 rounded-[20px] p-8 relative overflow-hidden" data-id="element-301">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-brand-blue-primary" data-id="element-302"></div>
            <div className="inline-block bg-brand-blue-primary/10 text-brand-blue-primary text-xs font-bold px-3 py-1 rounded-full mb-4" data-id="element-303">
              {t.b_badge}
            </div>
            <h3 className="font-display font-extrabold text-2xl text-brand-navy-dark mb-2" data-id="element-304">
              {t.b_name}
            </h3>
            <p className="text-brand-text-muted text-sm line-through mb-2" data-id="element-305">
              {t.b_old}
            </p>
            <div className="font-display font-black text-[3.2rem] text-brand-blue-primary leading-none mb-2" data-id="element-306">
              $500
            </div>
            <p className="text-brand-text-secondary text-sm mb-4" data-id="element-307">{t.b_note}</p>
            <div className="inline-block bg-brand-gold-champagne/10 text-brand-gold-champagne text-xs font-bold px-3 py-1 rounded-full mb-6" data-id="element-308">
              {t.b_save}
            </div>

            <div className="w-full h-px bg-brand-blue-primary/10 mb-6" data-id="element-309"></div>
            <p className="text-sm font-bold text-brand-navy-dark mb-4" data-id="element-310">
              {t.includes}
            </p>

            <div className="space-y-3 mb-8" data-id="element-311">
              {[{
              text: t.b_f1,
              inc: true
            }, {
              text: t.b_f2,
              inc: true
            }, {
              text: t.b_f3,
              inc: true
            }, {
              text: t.b_f4,
              inc: true
            }, {
              text: t.b_f5,
              inc: true
            }, {
              text: t.b_f6,
              inc: true
            }, {
              text: t.b_f7,
              inc: true
            }, {
              text: t.b_f8,
              inc: false
            }, {
              text: t.b_f9,
              inc: false
            }].map((f, i) => <div key={i} className="flex items-start space-x-3" data-id="element-312">
                  {f.inc ? <CheckCircle2 className="w-5 h-5 text-brand-blue-primary shrink-0" data-id="element-313" /> : <Minus className="w-5 h-5 text-gray-300 shrink-0" data-id="element-314" />}
                  <span className={`text-sm ${f.inc ? 'text-brand-text-secondary' : 'text-gray-400'}`} data-id="element-315">
                    {f.text}
                  </span>
                </div>)}
            </div>

            <p className="text-sm font-medium text-brand-navy-dark mb-6" data-id="element-316">
              {t.b_del}
            </p>

            <a href={`https://wa.me/16892876483?text=${encodeURIComponent(t.b_wa)}`} target="_blank" rel="noreferrer" className="block w-full text-center bg-gradient-button text-white py-3.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 mb-3" data-id="element-317">
              {t.b_btn}
            </a>
            <p className="text-center text-[0.7rem] text-brand-text-muted" data-id="element-318">
              {t.guarantee}
            </p>
          </motion.div>

          {/* Professional Plan (Featured) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-gradient-to-br from-brand-blue-primary/5 to-white border border-brand-blue-primary/30 rounded-[20px] p-8 relative overflow-hidden shadow-[0_0_40px_rgba(30,95,212,0.08)] lg:-mt-8 lg:mb-8" data-id="element-319">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-brand-blue-primary to-brand-blue-bright" data-id="element-320"></div>
            <div className="absolute top-0 right-0 bg-brand-blue-primary text-white text-xs font-bold px-4 py-1.5 rounded-bl-lg" data-id="element-321">
              {t.p_pop}
            </div>

            <div className="inline-block bg-brand-blue-primary/10 text-brand-blue-primary text-xs font-bold px-3 py-1 rounded-full mb-4 mt-2" data-id="element-322">
              {t.p_badge}
            </div>
            <h3 className="font-display font-extrabold text-2xl text-brand-navy-dark mb-2" data-id="element-323">
              {t.p_name}
            </h3>
            <p className="text-brand-text-muted text-sm line-through mb-2" data-id="element-324">
              {t.p_old}
            </p>
            <div className="font-display font-black text-[3.2rem] text-brand-blue-primary leading-none mb-2" data-id="element-325">
              $800
            </div>
            <p className="text-brand-text-secondary text-sm mb-4" data-id="element-326">{t.b_note}</p>
            <div className="inline-block bg-brand-gold-champagne/10 text-brand-gold-champagne text-xs font-bold px-3 py-1 rounded-full mb-6" data-id="element-327">
              {t.p_save}
            </div>

            <div className="w-full h-px bg-brand-blue-primary/20 mb-6" data-id="element-328"></div>
            <p className="text-sm font-bold text-brand-navy-dark mb-4" data-id="element-329">
              {t.p_inc}
            </p>

            <div className="space-y-3 mb-8" data-id="element-330">
              {[{
              text: t.p_f1,
              inc: true
            }, {
              text: t.p_f2,
              inc: true
            }, {
              text: t.p_f3,
              inc: true
            }, {
              text: t.p_f4,
              inc: true
            }, {
              text: t.p_f5,
              inc: true
            }, {
              text: t.p_f6,
              inc: true
            }, {
              text: t.p_f7,
              inc: false
            }, {
              text: t.p_f8,
              inc: false
            }].map((f, i) => <div key={i} className="flex items-start space-x-3" data-id="element-331">
                  {f.inc ? <CheckCircle2 className="w-5 h-5 text-brand-blue-primary shrink-0" data-id="element-332" /> : <Minus className="w-5 h-5 text-gray-300 shrink-0" data-id="element-333" />}
                  <span className={`text-sm ${f.inc ? 'text-brand-text-secondary font-medium' : 'text-gray-400'}`} data-id="element-334">
                    {f.text}
                  </span>
                </div>)}
            </div>

            <p className="text-sm font-medium text-brand-navy-dark mb-6" data-id="element-335">
              {t.p_del}
            </p>

            <a href={`https://wa.me/16892876483?text=${encodeURIComponent(t.p_wa)}`} target="_blank" rel="noreferrer" className="block w-full text-center bg-gradient-button text-white py-4 rounded-xl font-bold shadow-[0_8px_20px_rgba(30,95,212,0.3)] hover:shadow-[0_12px_25px_rgba(30,95,212,0.4)] transition-all hover:-translate-y-1 mb-3 text-lg" data-id="element-336">
              {t.p_btn}
            </a>
            <p className="text-center text-[0.7rem] text-brand-text-muted" data-id="element-337">
              {t.guarantee}
            </p>
          </motion.div>

          {/* Premium Plan */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white border border-brand-gold-champagne/30 rounded-[20px] p-8 relative overflow-hidden" data-id="element-338">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-gold" data-id="element-339"></div>
            <div className="inline-block bg-brand-gold-champagne/10 text-brand-gold-champagne text-xs font-bold px-3 py-1 rounded-full mb-4" data-id="element-340">
              {t.pr_badge}
            </div>
            <h3 className="font-display font-extrabold text-2xl text-brand-navy-dark mb-2" data-id="element-341">
              {t.pr_name}
            </h3>
            <p className="text-brand-text-muted text-sm line-through mb-2" data-id="element-342">
              {t.pr_old}
            </p>
            <div className="font-display font-black text-[3.2rem] text-brand-gold-champagne leading-none mb-2" data-id="element-343">
              $1,200
            </div>
            <p className="text-brand-text-secondary text-sm mb-4" data-id="element-344">{t.b_note}</p>
            <div className="inline-block bg-brand-gold-champagne/10 text-brand-gold-champagne text-xs font-bold px-3 py-1 rounded-full mb-6" data-id="element-345">
              {t.pr_save}
            </div>

            <div className="w-full h-px bg-brand-gold-champagne/20 mb-6" data-id="element-346"></div>
            <p className="text-sm font-bold text-brand-navy-dark mb-4" data-id="element-347">
              {t.includes}
            </p>

            <div className="space-y-3 mb-8" data-id="element-348">
              {[t.pr_f1, t.pr_f2, t.pr_f3, t.pr_f4, t.pr_f5, t.pr_f6, t.pr_f7, t.pr_f8].map((text, i) => <div key={i} className="flex items-start space-x-3" data-id="element-349">
                  <CheckCircle2 className="w-5 h-5 text-brand-gold-champagne shrink-0" data-id="element-350" />
                  <span className="text-sm text-brand-text-secondary" data-id="element-351">
                    {text}
                  </span>
                </div>)}
            </div>

            <p className="text-sm font-medium text-brand-navy-dark mb-6" data-id="element-352">
              {t.pr_del}
            </p>

            <a href={`https://wa.me/16892876483?text=${encodeURIComponent(t.pr_wa)}`} target="_blank" rel="noreferrer" className="block w-full text-center bg-gradient-gold text-black py-3.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 mb-3" data-id="element-353">
              {t.pr_btn}
            </a>
            <p className="text-center text-[0.7rem] text-brand-text-muted" data-id="element-354">
              {t.guarantee}
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}